import React, { FC, Fragment, useEffect, useState } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { LinearProgress } from '@mui/material';

import styles from './UserListPage.module.scss';
import { UserListPageProps } from './UserListPageProps';
import { UserService } from './UserService';
import { BaseUserModel } from 'models/BaseUserModel';
import { columns } from './UserTable/UserTableDefinition';
import { AddUserDialog } from 'pages/UserListPage/AddUserDialog/AddUserDialog';
import { UserDetailsDialog } from 'pages/UserListPage/UserDetailsDialog/UserDetailsDialog';
import { UserListCustomToolbar } from 'pages/UserListPage/UserTable/UserListCustomToolbar';
import { dataGridFocusStyles } from 'styles';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

export const UserListPage: FC<UserListPageProps> = () => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAddUserDialogOpen, setIsAddUserDialogSetOpen] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isUserDetailsDialogOpen, setIsUserDetailsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BaseUserModel | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [users, setUsers] = useState<BaseUserModel[]>([]);

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const fetchedUsers: BaseUserModel[] = await UserService.getUsers();

      if (Array.isArray(fetchedUsers)) {
        setUsers(fetchedUsers);
      } else {
        throw new Error('The fetched users data is not an array');
      }
    } catch (error) {
      console.error('An error occurred while fetching users:', error);
      setUsers([]);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'An error occurred while fetching users. Please refresh, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsLoading(false);
    }

  };

  const showDialog = () => {
    setIsAddUserDialogSetOpen(true);
  };

  const clickCellHandler = (params: any) => {
    if (params?.field === 'detailedInfo') {
      const user = params?.row;

      setSelectedUser(user);
      setIsUserDetailsDialogOpen(true);
    }
  }

  const toggleUserRoles = async (email: string, role: string, shouldAssign: boolean): Promise<void> => {
    setIsLoading(true);

    try {
      if (shouldAssign) {
        await UserService.assignRole(email, role);
      } else {
        await UserService.revokeRole(email, role);
      }

      await getUsers();
    } catch (error) {
      console.error('Failed to update user role', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed to update user role. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserStatus = async (email: string, shouldEnable: boolean): Promise<void> => {
    setIsLoading(true);

    try {
      if (shouldEnable) {
        await UserService.enable(email);
      } else {
        await UserService.disable(email);
      }

      await getUsers();
    } catch (error) {
      console.error('Failed to update user status', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed to update user status. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const userDetailsCloseHandler = (): void => {
    setSelectedUser(null);
    setIsUserDetailsDialogOpen(false);
  };

  return (
    <Fragment>
      { isAddUserDialogOpen &&
        <AddUserDialog
          setOpen={setIsAddUserDialogSetOpen}
          onUserCreation={getUsers}
        />
      }

      { isUserDetailsDialogOpen && selectedUser &&
        <UserDetailsDialog
          onClose={userDetailsCloseHandler}
          user={selectedUser}
        />
      }

      <div className={styles.UserListPage} data-testid="UserListPage">
          <DataGrid
            rows={users}
            columns={columns(toggleUserRoles, toggleUserStatus)}
            slots={{
              loadingOverlay: LinearProgress,
              toolbar: () => UserListCustomToolbar(showDialog)
            }}
            loading={isLoading}
            className="project-data-grid"
            sx={dataGridFocusStyles}
            disableRowSelectionOnClick
            onCellClick={(params, event, details) => clickCellHandler(params)}
            // onRowClick={(params, event, details) => clickRowHandler(params, event, details)}
          />

        { isAlertDialogOpen && currentAlert &&
          <AlertDialog
            onClose={() => {
              setIsAlertDialogOpen(false);
              setCurrentAlert(null);
            }}
            title={currentAlert.title}
            message={currentAlert.message}
          ></AlertDialog>
        }
      </div>
    </Fragment>
  );
};


